import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUp, CalendarClock, Clock, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import type { client } from "@duress/shared";
import { useClients, useRecentlyViewedClients } from "@/features/clients/useClients";
import { useAuth } from "@/features/auth/useAuth";
import {
  bandFor,
  BAND_META,
  summarisePortfolio,
  type HealthBand,
} from "./health";
import { cn } from "@/lib/utils";
import { isStale, daysSinceLatestPeriod } from "@/lib/format";

const RECENT_LIMIT = 5;
const AT_RISK_BANDS: HealthBand[] = ["at_risk", "needs_attention"];
const RENEWAL_WINDOW_DAYS = 90;

function daysUntil(dateStr: string): number {
  const end = new Date(dateStr + "T00:00:00");
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
}

// Composite urgency: health band severity + upcoming renewal proximity + declining trend.
// Higher = more urgent. Used to sort the At-risk panel.
export function urgencyScore(c: client.ClientListItem): number {
  const band = c.healthScore !== null ? bandFor(c.healthScore) : null;
  const bandPts = band === "at_risk" ? 3 : band === "needs_attention" ? 2 : 0;

  let renewalPts = 0;
  if (c.contractEndDate) {
    const days = daysUntil(c.contractEndDate);
    if (days >= 0 && days <= 30) renewalPts = 3;
    else if (days <= 60) renewalPts = 2;
    else if (days <= 90) renewalPts = 1;
  }

  const trendPts =
    c.healthScore !== null && c.previousHealthScore !== null && c.healthScore < c.previousHealthScore
      ? 2
      : 0;

  return bandPts + renewalPts + trendPts;
}

function formatRenewalDate(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(d ?? "1", 10)} ${months[parseInt(m ?? "1", 10) - 1] ?? ""}`;
}

export function OverviewPage(): React.JSX.Element {
  const { data: allClients, isLoading, error, refetch } = useClients();
  const { data: recentlyViewed } = useRecentlyViewedClients();
  const { user } = useAuth();
  const [portfolioFilter, setPortfolioFilter] = useState<"all" | "mine">("all");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="table" />
      </div>
    );
  }
  if (error) {
    return <ErrorCard message="Couldn't load portfolio." onRetry={refetch} />;
  }
  if (!allClients || allClients.length === 0) {
    return <EmptyOverview />;
  }

  const clients =
    portfolioFilter === "mine" && user?.internalUserId
      ? allClients.filter((c) => c.csmUserId === user.internalUserId)
      : allClients;

  const summary = summarisePortfolio(clients);
  const atRisk = clients
    .filter((c) => c.healthScore !== null && AT_RISK_BANDS.includes(bandFor(c.healthScore)))
    .sort((a, b) => urgencyScore(b) - urgencyScore(a) || (a.healthScore ?? 0) - (b.healthScore ?? 0));

  const staleCount = clients.filter((c) => isStale(c.latestPeriodAt)).length;

  const withDelta = clients
    .filter(
      (c) => c.healthScore !== null && c.previousHealthScore !== null,
    )
    .map((c) => ({
      client: c,
      delta: (c.healthScore as number) - (c.previousHealthScore as number),
    }))
    .filter((row) => row.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, RECENT_LIMIT);

  const upcomingRenewals = clients
    .filter((c) => {
      if (!c.contractEndDate) return false;
      const days = daysUntil(c.contractEndDate);
      return days >= 0 && days <= RENEWAL_WINDOW_DAYS;
    })
    .sort((a, b) => {
      const da = daysUntil(a.contractEndDate!);
      const db = daysUntil(b.contractEndDate!);
      return da - db;
    });

  const heading = portfolioFilter === "mine" ? "My portfolio" : "Overview";
  const subheading =
    portfolioFilter === "mine"
      ? `Your assigned clients — ${summary.total} ${summary.total === 1 ? "client" : "clients"}.`
      : `Portfolio health across ${summary.total} ${summary.total === 1 ? "client" : "clients"}.`;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-head font-semibold tracking-tight">{heading}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subheading}</p>
        </div>
        {user?.internalUserId ? (
          <div className="flex items-center rounded-md border bg-background">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPortfolioFilter("all")}
              className={portfolioFilter === "all" ? "text-foreground" : "text-muted-foreground"}
            >
              All clients
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPortfolioFilter("mine")}
              className={portfolioFilter === "mine" ? "text-foreground" : "text-muted-foreground"}
            >
              My portfolio
            </Button>
          </div>
        ) : null}
      </div>

      {portfolioFilter === "mine" && clients.length === 0 ? (
        <p className="text-sm text-muted-foreground">No clients are assigned to you yet.</p>
      ) : (
        <>
          <PortfolioSummaryPanel summary={summary} staleCount={staleCount} />

          <div className="grid gap-6 lg:grid-cols-2">
            <AtRiskPanel clients={atRisk} />
            <RecentChangesPanel
              rows={withDelta}
              scoredCount={summary.scored}
              totalCount={summary.total}
            />
          </div>

          {upcomingRenewals.length > 0 ? (
            <UpcomingRenewalsPanel clients={upcomingRenewals} />
          ) : null}

          {portfolioFilter === "all" && recentlyViewed && recentlyViewed.length > 0 ? (
            <RecentlyViewedPanel clients={recentlyViewed} />
          ) : null}
        </>
      )}
    </div>
  );
}

function EmptyOverview(): React.JSX.Element {
  return (
    <div className="card-duress p-12 text-center">
      <h1 className="text-2xl font-head font-semibold tracking-tight">Overview</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Add a client to start tracking portfolio health.
      </p>
      <Link
        to="/"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-pill-orange hover:underline"
      >
        Go to clients
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function PortfolioSummaryPanel({
  summary,
  staleCount,
}: {
  summary: ReturnType<typeof summarisePortfolio>;
  staleCount: number;
}): React.JSX.Element {
  return (
    <section className="card-duress p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-head text-lg font-semibold">Portfolio health</h2>
        <div className="flex items-center gap-4">
          {staleCount > 0 ? (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {staleCount} {staleCount === 1 ? "client has" : "clients have"} data older than 60 days
            </p>
          ) : null}
          <p className="text-xs section-label">
            {summary.scored} of {summary.total} clients have a current period
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryStat
          label="Average score"
          value={summary.averageScore !== null ? String(summary.averageScore) : "—"}
        />
        <SummaryStat
          label={BAND_META.excellent.label}
          value={String(summary.excellent)}
          dotClass={BAND_META.excellent.dotClass}
          textClass={BAND_META.excellent.textClass}
        />
        <SummaryStat
          label={BAND_META.healthy.label}
          value={String(summary.healthy)}
          dotClass={BAND_META.healthy.dotClass}
          textClass={BAND_META.healthy.textClass}
        />
        <SummaryStat
          label={BAND_META.needs_attention.label}
          value={String(summary.needsAttention)}
          dotClass={BAND_META.needs_attention.dotClass}
          textClass={BAND_META.needs_attention.textClass}
        />
        <SummaryStat
          label={BAND_META.at_risk.label}
          value={String(summary.atRisk)}
          dotClass={BAND_META.at_risk.dotClass}
          textClass={BAND_META.at_risk.textClass}
        />
      </div>

      <BandDistributionBar summary={summary} />
    </section>
  );
}

function BandDistributionBar({
  summary,
}: {
  summary: ReturnType<typeof summarisePortfolio>;
}): React.JSX.Element | null {
  const total = summary.excellent + summary.healthy + summary.needsAttention + summary.atRisk;
  if (total === 0) return null;

  const bands: Array<{ band: HealthBand; count: number }> = [
    { band: "excellent", count: summary.excellent },
    { band: "healthy", count: summary.healthy },
    { band: "needs_attention", count: summary.needsAttention },
    { band: "at_risk", count: summary.atRisk },
  ];
  const distributionLabel = bands
    .filter((b) => b.count > 0)
    .map((b) => `${BAND_META[b.band].label}: ${b.count}`)
    .join(", ");

  return (
    <div
      className="mt-4 flex h-2 w-full overflow-hidden rounded-full"
      role="img"
      aria-label={`Health distribution — ${distributionLabel}`}
    >
      {bands.map(({ band, count }) =>
        count > 0 ? (
          <div
            key={band}
            className={BAND_META[band].dotClass}
            style={{ width: `${(count / total) * 100}%` }}
            title={`${BAND_META[band].label}: ${count}`}
          />
        ) : null,
      )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  dotClass,
  textClass,
}: {
  label: string;
  value: string;
  dotClass?: string;
  textClass?: string;
}): React.JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2">
        {dotClass ? <span className={cn("h-2.5 w-2.5 rounded-full", dotClass)} /> : null}
        <span className="section-label">{label}</span>
      </div>
      <div className={cn("num mt-1 text-3xl font-semibold", textClass ?? "text-foreground")}>
        {value}
      </div>
    </div>
  );
}

// Shared shell for all list rows — name + industries on the left, arbitrary right-side content.
function ClientRowShell({
  client: c,
  right,
}: {
  client: client.ClientListItem;
  right: React.ReactNode;
}): React.JSX.Element {
  return (
    <li className="py-3">
      <Link
        to={`/clients/${c.slug}`}
        className="group flex items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground group-hover:text-phoenix-orange">
            {c.name}
          </p>
          {c.industries.length > 0 ? (
            <p className="truncate text-xs text-muted-foreground">
              {c.industries.join(", ")}
            </p>
          ) : null}
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">{right}</div>
      </Link>
    </li>
  );
}

function AtRiskPanel({
  clients,
}: {
  clients: client.ClientListItem[];
}): React.JSX.Element {
  return (
    <section className="card-duress p-6">
      <h2 className="font-head text-lg font-semibold">At-risk accounts</h2>
      <p className="mt-1 text-xs section-label">
        Clients in the At Risk or Needs Attention bands
      </p>

      {clients.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No clients are at risk or need attention. Nice.
        </p>
      ) : (
        <ul className="mt-4 divide-y">
          {clients.map((c) => (
            <ClientRow key={c.id} client={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentChangesPanel({
  rows,
  scoredCount,
  totalCount,
}: {
  rows: Array<{ client: client.ClientListItem; delta: number }>;
  scoredCount: number;
  totalCount: number;
}): React.JSX.Element {
  return (
    <section className="card-duress p-6">
      <h2 className="font-head text-lg font-semibold">Recent changes</h2>
      <p className="mt-1 text-xs section-label">
        Clients whose health score has shifted between the last two periods
      </p>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          {scoredCount < totalCount
            ? "Upload more periods to see changes here."
            : "No score changes between the last two periods."}
        </p>
      ) : (
        <ul className="mt-4 divide-y">
          {rows.map(({ client: c, delta }) => (
            <ClientRow key={c.id} client={c} delta={delta} />
          ))}
        </ul>
      )}
    </section>
  );
}

// Tiny trend arrow derived from the two-period delta — green up, red down.
function TrendArrow({ client: c }: { client: client.ClientListItem }): React.JSX.Element | null {
  if (c.healthScore === null || c.previousHealthScore === null) return null;
  const delta = c.healthScore - c.previousHealthScore;
  if (delta === 0) return null;
  const Icon = delta > 0 ? ArrowUp : ArrowDown;
  return (
    <Icon
      className={cn("h-3.5 w-3.5 flex-shrink-0", delta > 0 ? "text-pill-green" : "text-pill-red")}
      aria-hidden="true"
    />
  );
}

// Clock icon shown when the latest period data is older than the staleness threshold.
function StaleIndicator({
  latestPeriodAt,
}: {
  latestPeriodAt: string | null | undefined;
}): React.JSX.Element | null {
  if (!isStale(latestPeriodAt)) return null;
  const days = latestPeriodAt ? daysSinceLatestPeriod(latestPeriodAt) : 0;
  return (
    <span
      role="img"
      aria-label={`Data is ${days} days old`}
      title={`Data is ${days} days old`}
      className="flex-shrink-0"
    >
      <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
    </span>
  );
}

function ClientRow({
  client: c,
  delta,
}: {
  client: client.ClientListItem;
  delta?: number;
}): React.JSX.Element {
  const score = c.healthScore;
  const band = score !== null ? bandFor(score) : null;
  const meta = band ? BAND_META[band] : null;

  return (
    <ClientRowShell
      client={c}
      right={
        <>
          {delta === undefined ? <TrendArrow client={c} /> : null}
          <StaleIndicator latestPeriodAt={c.latestPeriodAt} />
          {delta !== undefined ? <DeltaPill delta={delta} /> : null}
          {meta ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                meta.bgClass,
                meta.textClass,
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", meta.dotClass)} />
              {meta.label}
            </span>
          ) : null}
          <span className="num w-10 text-right text-base font-semibold tabular-nums">
            {score ?? "—"}
          </span>
        </>
      }
    />
  );
}

function UpcomingRenewalsPanel({
  clients,
}: {
  clients: client.ClientListItem[];
}): React.JSX.Element {
  return (
    <section className="card-duress p-6">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-pill-orange" />
        <h2 className="font-head text-lg font-semibold">Upcoming renewals</h2>
      </div>
      <p className="mt-1 text-xs section-label">
        Contracts renewing within the next {RENEWAL_WINDOW_DAYS} days
      </p>
      <ul className="mt-4 divide-y">
        {clients.map((c) => (
          <RenewalRow key={c.id} client={c} />
        ))}
      </ul>
    </section>
  );
}

function RenewalRow({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const days = daysUntil(c.contractEndDate!);
  const urgentClass =
    days <= 14
      ? "text-pill-red"
      : days <= 30
        ? "text-pill-orange"
        : "text-muted-foreground";

  return (
    <ClientRowShell
      client={c}
      right={
        <>
          <span className={cn("text-xs font-medium tabular-nums", urgentClass)}>
            {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `in ${days} days`}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatRenewalDate(c.contractEndDate!)}
          </span>
        </>
      }
    />
  );
}

function RecentlyViewedPanel({
  clients,
}: {
  clients: client.ClientListItem[];
}): React.JSX.Element {
  return (
    <section className="card-duress p-6">
      <h2 className="font-head text-lg font-semibold">Recently viewed</h2>
      <p className="mt-1 text-xs section-label">
        Clients you've visited recently
      </p>
      <ul className="mt-4 divide-y">
        {clients.map((c) => (
          <ClientRow key={c.id} client={c} />
        ))}
      </ul>
    </section>
  );
}

function DeltaPill({ delta }: { delta: number }): React.JSX.Element {
  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
  const cls =
    delta > 0
      ? "text-pill-green bg-falcon-green/10"
      : delta < 0
        ? "text-pill-red bg-emergency-red/10"
        : "text-muted-foreground bg-muted";
  const sign = delta > 0 ? "+" : "";
  return (
    <span
      className={cn(
        "num inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums",
        cls,
      )}
    >
      <Icon className="h-3 w-3" />
      {sign}
      {delta}
    </span>
  );
}
